import React, { FC, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Divider, Typography, Grid } from "@mui/material";
import {
  DE,
  NA_COUNTRIES,
  WORH_MOBILE_FOOTER_CATEGORY_ID,
  omitLinksForUK
} from "@RHCommerceDev/utils/constants";
import useUserPreferences from "@RHCommerceDev/hooks/useUserPreferences";
import ExternalLinkArrowIcon from "@RHCommerceDev/icon-external-link-arrow";
import useTypographyStyles from "@RHCommerceDev/hooks/useTypographyStyles";
import clsx from "clsx";
import { queryWORHMenu } from "@RHCommerceDev/graphql-client/queries/navigation";
import { filterArrayRecursivelyWith } from "@RHCommerceDev/utils/filterArrayRecursivelyWith";
import cloneDeep from "lodash.clonedeep";
import { ClassNameMap } from "@mui/material";
import memoize from "@RHCommerceDev/utils/memoize";
import useLocale from "@RHCommerceDev/hooks-use-locale/useLocale";
import getCountryFromUrl from "@RHCommerceDev/utils/getCountryFromUrl";
import EmailSignup from "./EmailSignup";
import { useRhUserAtomValue } from "@RHCommerceDev/hooks/atoms";

const RHRFooterContent: FC<{ classes: ClassNameMap; brand?: string }> = ({
  classes,
  brand
}) => {
  const { country } = useUserPreferences();
  const { userType } = useRhUserAtomValue();
  const locale = useLocale();
  const urlCountry = getCountryFromUrl();

  const showEmailSignupComponent = useMemo((): boolean => {
    if (NA_COUNTRIES.includes(urlCountry)) {
      return true;
    }
    if (urlCountry === "GB") {
      return true;
    }
    if (urlCountry === DE) {
      return true;
    }
    return false;
  }, [urlCountry]);

  const typographyclasses = useTypographyStyles({
    keys: ["rhBaseBody1", "rhBaseBody2", "rhBaseCaption"]
  });

  const { data: { worhMenu } = {} as Query } = useQuery<Query>(queryWORHMenu, {
    variables: {
      categoryId: WORH_MOBILE_FOOTER_CATEGORY_ID,
      locale
    }
  });

  const worhMenuMemorized = useMemo(() => {
    const isUK = country === "GB";

    const filteredChildCategories =
      worhMenu?.childCategories?.filter((childCategory: NavigationElement) => {
        return childCategory?.childCategories?.length;
      }) ?? [];

    const newWorhMenu = {
      ...worhMenu,
      childCategories: filteredChildCategories
    };

    if (isUK) {
      return {
        ...worhMenu,
        childCategories: filterArrayRecursivelyWith({
          array: cloneDeep(newWorhMenu?.childCategories),
          nestedKey: "childCategories",
          cb: (childCategory: NavigationElement) => {
            return !omitLinksForUK?.includes(
              childCategory?.displayName?.toUpperCase() ?? ""
            );
          }
        })?.filter(
          (childCategory: NavigationElement) =>
            childCategory?.childCategories?.length
        )
      };
    }

    return {
      ...worhMenu,
      childCategories:
        worhMenu?.childCategories?.filter(
          (childCategory: NavigationElement) =>
            childCategory?.childCategories?.length
        ) ?? []
    };
  }, [country, worhMenu]);

  return (
    worhMenuMemorized && (
      <footer>
        <div
          className={clsx([
            classes.footerContainer,
            showEmailSignupComponent && classes.footerContainerwithEmail
          ])}
        >
          <div
            className={clsx([
              classes.separator,
              showEmailSignupComponent && classes.marketingSeparator
            ])}
          >
            <Divider />
          </div>
          {showEmailSignupComponent && <EmailSignup />}
          <div
            className={clsx([
              classes.footerMenus,
              showEmailSignupComponent && classes.footerMenusWithemail
            ])}
          >
            <Grid
              container
              justifyContent="space-between"
              className={classes.grid} /* "grid grid-wide" */
            >
              {worhMenuMemorized?.childCategories?.map(
                ({ displayName, childCategories }, i) => (
                  <div key={`child_category_${i}`}>
                    <div className={classes.gridItemInner}>
                      <Typography
                        className={clsx([
                          typographyclasses.rhBaseBody2,
                          classes.footerHeader
                        ])}
                      >
                        {displayName}
                      </Typography>
                      <ul>
                        {childCategories?.map(
                          (
                            {
                              displayName: subCategoryName,
                              targetUrl,
                              linkType
                            },
                            j
                          ) => {
                            const isExternalLink =
                              targetUrl.startsWith("https://");

                            return !(
                              targetUrl?.includes("gift-registry") &&
                              (brand === "TN" ||
                                userType === "CONTRACT" ||
                                userType === "TRADE")
                            ) ? (
                              <Typography
                                variant="caption"
                                key={`child_sub_category_${i}_${j}`}
                              >
                                <li className={classes.footerItems}>
                                  <a
                                    target={
                                      linkType === "none" || isExternalLink
                                        ? "_blank"
                                        : "_self"
                                    }
                                    href={targetUrl}
                                    data-analytics-parentid={displayName}
                                  >
                                    {subCategoryName}
                                    {isExternalLink && (
                                      <ExternalLinkArrowIcon
                                        width={6.21}
                                        height={6.21}
                                        aria-hidden="false"
                                      />
                                    )}
                                  </a>
                                </li>
                              </Typography>
                            ) : null;
                          }
                        )}
                      </ul>
                    </div>
                  </div>
                )
              )}
            </Grid>
          </div>
        </div>
      </footer>
    )
  );
};

export default memoize(RHRFooterContent);
